<template>
  <b-form :class="{ 'checkout-closing-form': isClosingCheckout }">
    <div v-if="isClosingCheckout" class="closing-intro">
      <span class="closing-intro-icon fas fa-cash-register" />
      <div>
        <h5 class="mb-1">Conferência final do caixa</h5>
        <p class="mb-0">{{ closingIntroText }}</p>
      </div>
    </div>

    <b-alert
      v-if="isOpeningCheckout && openingContext"
      show
      :variant="openingContext.canOpen ? 'info' : 'danger'"
    >
      <span
        :class="[
          'fas mr-1',
          openingContext.canOpen ? 'fa-link' : 'fa-exclamation-triangle'
        ]"
      />
      {{ openingContextMessage }}
    </b-alert>

    <section :class="{ 'closing-section': isClosingCheckout }">
      <div v-if="isClosingCheckout" class="closing-section-heading">
        <span class="closing-section-icon fas fa-wallet" />
        <div>
          <strong>Saldo contado</strong>
          <small>Valor em dinheiro encontrado no caixa.</small>
        </div>
      </div>
      <b-form-group
        :label="isClosingCheckout ? null : 'Valor de abertura'"
        :label-for="field"
        :class="[validationClass, { 'mb-0': isClosingCheckout }]"
      >
        <b-input-group prepend="R$">
          <b-form-input
            :id="field"
            v-model="form[field]"
            v-money="{
              decimal: ',',
              thousands: '.',
              precision: 2
            }"
            :disabled="isOpeningValueReadOnly"
            :state="validation[field]"
          />
        </b-input-group>
        <b-form-invalid-feedback v-if="$v.form[field].$invalid">
          Informe um valor valido
        </b-form-invalid-feedback>
      </b-form-group>

      <template v-if="isOpeningCheckout && canOverrideOpening">
        <b-form-checkbox
          v-model="form.openingOverrideEnabled"
          switch
          class="mt-3"
          @change="onOpeningOverrideChange"
        >
          Alterar o valor herdado do fechamento anterior
        </b-form-checkbox>
        <b-form-group
          v-if="form.openingOverrideEnabled && openingHasDifference"
          label="Motivo da alteração"
          label-for="opening-override-reason"
          class="mt-3"
          :class="openingReasonValidationClass"
        >
          <b-form-textarea
            id="opening-override-reason"
            v-model.trim="form.openingOverrideReason"
            rows="2"
            maxlength="500"
            :state="validation.openingOverrideReason"
            placeholder="Explique por que a abertura será diferente do último fechamento"
          />
          <b-form-invalid-feedback>
            Informe o motivo da alteração do valor.
          </b-form-invalid-feedback>
        </b-form-group>
      </template>

      <div v-if="isClosingCheckout" class="closing-balance-summary">
        <div>
          <small>Saldo esperado</small>
          <strong>{{ closingExpectedValue | formatCurrency }}</strong>
        </div>
        <div :class="closingDifferenceClass">
          <small>{{ closingDifferenceLabel }}</small>
          <strong>{{ closingDifferenceAbsolute | formatCurrency }}</strong>
        </div>
      </div>

      <b-form-group
        v-if="isClosingCheckout && closingHasDifference"
        label="Motivo da diferença"
        label-for="closing-difference-reason"
        class="mt-3"
        :class="closingReasonValidationClass"
      >
        <b-form-textarea
          id="closing-difference-reason"
          v-model.trim="form.closingDifferenceReason"
          rows="2"
          maxlength="500"
          :state="validation.closingDifferenceReason"
          placeholder="Descreva a causa da falta ou sobra encontrada"
        />
        <b-form-invalid-feedback>
          Informe o motivo da diferença encontrada.
        </b-form-invalid-feedback>
      </b-form-group>
    </section>

    <section
      v-if="hasActiveProducts"
      :class="{ 'closing-section': isClosingCheckout }"
    >
      <div v-if="isClosingCheckout" class="closing-section-heading">
        <span class="closing-section-icon fas fa-boxes" />
        <div>
          <strong>Estoque final</strong>
          <small>Quantidade de produtos restante no fechamento.</small>
        </div>
      </div>
      <b-form-group
        :label="isClosingCheckout ? null : 'Quantidade de Produtos'"
        :label-for="productField"
        :class="[productValidationClass, { 'mb-0': isClosingCheckout }]"
      >
        <b-input-group>
          <b-form-input
            :id="productField"
            v-model="form[productField]"
            v-money="{
              decimal: ',',
              thousands: '.',
              precision: 0
            }"
            :state="validation[productField]"
          />
        </b-input-group>
        <b-form-invalid-feedback v-if="$v.form[productField].$invalid">
          Informe a quantidade total de produtos
        </b-form-invalid-feedback>
      </b-form-group>
    </section>

    <div v-if="isClosingCheckout" class="closing-uploads-grid">
      <b-form-group
        v-if="hasCheckoutClosingNotebookPhotos"
        label-for="closing-notebook-photos"
        :class="['closing-upload-section', closingNotebookValidationClass]"
      >
        <div class="closing-upload-heading">
          <span class="closing-section-icon fas fa-book" />
          <div class="closing-upload-title">
            <strong>Caderno de fechamento</strong>
            <small>{{ closingNotebookHelpText }}</small>
          </div>
          <b-badge
            pill
            :variant="
              isCheckoutClosingNotebookPhotoRequired ? 'warning' : 'light'
            "
          >
            {{
              isCheckoutClosingNotebookPhotoRequired
                ? 'Obrigatório'
                : 'Opcional'
            }}
          </b-badge>
        </div>
        <b-form-file
          id="closing-notebook-photos"
          accept="image/*"
          browse-text="Selecionar fotos"
          placeholder="Nenhuma foto selecionada"
          drop-placeholder="Solte as fotos aqui"
          multiple
          :state="validation.closingNotebookPhotos"
          @input="onPhotosInput('closingNotebookPhotos', $event)"
        />
        <b-alert
          v-if="photoInputErrors.closingNotebookPhotos"
          show
          variant="danger"
          class="py-2 mt-2 mb-0"
        >
          {{ photoInputErrors.closingNotebookPhotos }}
        </b-alert>
        <div class="photo-upload-actions">
          <b-button
            size="sm"
            variant="outline-primary"
            :disabled="form.closingNotebookPhotos.length >= MAX_PHOTOS_PER_TYPE"
            @click="startPhotoCamera('closingNotebookPhotos')"
          >
            <span class="fas fa-camera mr-1" />Tirar foto
          </b-button>
        </div>
        <small
          v-if="photoProcessing.closingNotebookPhotos"
          class="photo-selection-status"
        >
          <b-spinner small class="mr-1" />Otimizando fotos para envio...
        </small>
        <small v-else class="photo-selection-status">
          <span class="fas fa-images mr-1" />
          {{ photoCountLabel(form.closingNotebookPhotos) }}
        </small>
        <b-form-invalid-feedback v-if="$v.form.closingNotebookPhotos.$invalid">
          {{ closingNotebookInvalidMessage }}
        </b-form-invalid-feedback>
        <div
          v-for="(_, index) in form.closingNotebookPhotos"
          :key="`notebook-description-${index}`"
          class="photo-description mt-2"
        >
          <b-form-input
            v-model="form.closingNotebookDescriptions[index]"
            :placeholder="`Descrição opcional da foto ${index + 1}`"
          />
        </div>
      </b-form-group>

      <b-form-group
        v-if="hasCheckoutMachineReceiptPhotos"
        label-for="machine-receipt-photos"
        :class="['closing-upload-section', machineReceiptValidationClass]"
      >
        <div class="closing-upload-heading">
          <span class="closing-section-icon fas fa-receipt" />
          <div class="closing-upload-title">
            <strong>Comprovante de maquininha</strong>
            <small>{{ machineReceiptHelpText }}</small>
          </div>
          <b-badge
            pill
            :variant="
              isCheckoutMachineReceiptPhotoRequired ? 'warning' : 'light'
            "
          >
            {{
              isCheckoutMachineReceiptPhotoRequired ? 'Obrigatório' : 'Opcional'
            }}
          </b-badge>
        </div>
        <b-form-file
          id="machine-receipt-photos"
          accept="image/*"
          browse-text="Selecionar fotos"
          placeholder="Nenhuma foto selecionada"
          drop-placeholder="Solte as fotos aqui"
          multiple
          :state="validation.machineReceiptPhotos"
          @input="onPhotosInput('machineReceiptPhotos', $event)"
        />
        <b-alert
          v-if="photoInputErrors.machineReceiptPhotos"
          show
          variant="danger"
          class="py-2 mt-2 mb-0"
        >
          {{ photoInputErrors.machineReceiptPhotos }}
        </b-alert>
        <div class="photo-upload-actions">
          <b-button
            size="sm"
            variant="outline-primary"
            :disabled="form.machineReceiptPhotos.length >= MAX_PHOTOS_PER_TYPE"
            @click="startPhotoCamera('machineReceiptPhotos')"
          >
            <span class="fas fa-camera mr-1" />Tirar foto
          </b-button>
        </div>
        <small
          v-if="photoProcessing.machineReceiptPhotos"
          class="photo-selection-status"
        >
          <b-spinner small class="mr-1" />Otimizando fotos para envio...
        </small>
        <small v-else class="photo-selection-status">
          <span class="fas fa-images mr-1" />
          {{ photoCountLabel(form.machineReceiptPhotos) }}
        </small>
        <b-form-invalid-feedback v-if="$v.form.machineReceiptPhotos.$invalid">
          {{ machineReceiptInvalidMessage }}
        </b-form-invalid-feedback>
        <div
          v-for="(_, index) in form.machineReceiptPhotos"
          :key="`machine-description-${index}`"
          class="photo-description mt-2"
        >
          <b-form-input
            v-model="form.machineReceiptDescriptions[index]"
            :placeholder="`Descrição opcional da foto ${index + 1}`"
          />
        </div>
      </b-form-group>
    </div>

    <section v-if="cameraTarget" class="closing-camera-panel">
      <div class="closing-camera-heading">
        <div>
          <strong>
            <span class="fas fa-camera mr-1" />
            {{ cameraTargetLabel }}
          </strong>
          <small>Capture a imagem e confirme antes de continuar.</small>
        </div>
        <b-button
          size="sm"
          variant="link"
          title="Fechar câmera"
          @click="closePhotoCamera"
        >
          <span class="fas fa-times" />
        </b-button>
      </div>

      <b-alert v-if="cameraError" show variant="danger" class="py-2">
        <span class="fas fa-exclamation-triangle mr-1" />{{ cameraError }}
      </b-alert>

      <div v-if="cameraLoading" class="closing-camera-loading">
        <b-spinner small class="mr-2" />Abrindo câmera...
      </div>

      <video
        v-show="cameraActive"
        ref="checkoutPhotoVideo"
        class="closing-camera-media"
        autoplay
        muted
        playsinline
      />

      <img
        v-if="cameraPreviewUrl"
        :src="cameraPreviewUrl"
        class="closing-camera-media"
        alt="Pré-visualização da foto capturada"
      />

      <div class="closing-camera-actions">
        <b-button
          v-if="cameraActive"
          variant="primary"
          @click="capturePhotoFromCamera"
        >
          <span class="fas fa-camera mr-1" />Capturar
        </b-button>
        <template v-if="cameraCapturedFile">
          <b-button variant="outline-secondary" @click="retakePhoto">
            <span class="fas fa-redo mr-1" />Tirar novamente
          </b-button>
          <b-button variant="success" @click="confirmCameraPhoto">
            <span class="fas fa-check mr-1" />Usar foto
          </b-button>
        </template>
      </div>
    </section>
  </b-form>
</template>

<script>
import { CHECKOUT_OPERATIONS } from '@/constants'
import withFormValidation from '@/mixins/withFormValidation'
import {
  formatCurrency,
  formatCurrencyInput,
  parseCurrency
} from '@/helpers/numbers'
import { mapGetters } from 'vuex'

const MAX_PHOTOS_PER_TYPE = 3
const MAX_PHOTO_SIZE = 20 * 1024 * 1024
const CLOUDINARY_SAFE_PHOTO_SIZE = 9 * 1024 * 1024
const CAMERA_MAX_DIMENSION = 2500

export default {
  name: 'CheckoutForm',
  filters: { formatCurrency },
  mixins: [
    withFormValidation({
      fields: [
        'openingValue',
        'openingOverrideReason',
        'openingProductCount',
        'closingValue',
        'closingDifferenceReason',
        'closingProductCount',
        'closingNotebookPhotos',
        'machineReceiptPhotos'
      ]
    })
  ],
  validations: {
    form: {
      openingValue: {
        required(value) {
          return (
            !this.isOpeningCheckout ||
            (value !== null &&
              value !== '' &&
              Number.isFinite(parseCurrency(value)) &&
              parseCurrency(value) >= 0)
          )
        }
      },
      openingOverrideReason: {
        required(value) {
          return !this.openingHasDifference || Boolean(value?.trim())
        }
      },
      closingValue: {
        required(value) {
          return (
            !this.isClosingCheckout ||
            (value !== null &&
              value !== '' &&
              Number.isFinite(parseCurrency(value)) &&
              parseCurrency(value) >= 0)
          )
        }
      },
      closingDifferenceReason: {
        required(value) {
          return !this.closingHasDifference || Boolean(value?.trim())
        }
      },
      openingProductCount: {
        required(value) {
          return (
            !this.hasActiveProducts ||
            !this.isOpeningCheckout ||
            (this.isOpeningCheckout && Number(value) >= 0)
          )
        }
      },
      closingProductCount: {
        required(value) {
          return (
            !this.hasActiveProducts ||
            !this.isClosingCheckout ||
            (this.isClosingCheckout && Number(value) >= 0)
          )
        }
      },
      closingNotebookPhotos: {
        valid(value) {
          if (!this.isClosingCheckout) return true
          if (!this.hasCheckoutClosingNotebookPhotos) return true
          if (this.photoProcessing.closingNotebookPhotos) return false
          return this.validatePhotos(
            value,
            this.isCheckoutClosingNotebookPhotoRequired
          )
        }
      },
      machineReceiptPhotos: {
        valid(value) {
          if (!this.isClosingCheckout) return true
          if (!this.hasCheckoutMachineReceiptPhotos) return true
          if (this.photoProcessing.machineReceiptPhotos) return false
          return this.validatePhotos(
            value,
            this.isCheckoutMachineReceiptPhotoRequired
          )
        }
      }
    }
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    operation: {
      type: String,
      required: true
    },
    storeConfig: {
      type: Object,
      default: null
    },
    openingContext: {
      type: Object,
      default: null
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    hasActiveProducts: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      MAX_PHOTOS_PER_TYPE,
      cameraTarget: null,
      cameraActive: false,
      cameraLoading: false,
      cameraRequestId: 0,
      cameraStream: null,
      cameraCapturedFile: null,
      cameraPreviewUrl: null,
      cameraError: null,
      photoProcessing: {
        closingNotebookPhotos: false,
        machineReceiptPhotos: false
      },
      photoProcessingRequestId: {
        closingNotebookPhotos: 0,
        machineReceiptPhotos: 0
      },
      photoInputErrors: {
        closingNotebookPhotos: null,
        machineReceiptPhotos: null
      }
    }
  },
  computed: {
    ...mapGetters('store', {
      storeHasCheckoutClosingNotebookPhotos: 'hasCheckoutClosingNotebookPhotos',
      storeIsCheckoutClosingNotebookPhotoRequired:
        'isCheckoutClosingNotebookPhotoRequired',
      storeHasCheckoutMachineReceiptPhotos: 'hasCheckoutMachineReceiptPhotos',
      storeIsCheckoutMachineReceiptPhotoRequired:
        'isCheckoutMachineReceiptPhotoRequired'
    }),
    form: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    },
    isOpeningCheckout() {
      return this.operation === CHECKOUT_OPERATIONS.OPEN
    },
    isClosingCheckout() {
      return this.operation === CHECKOUT_OPERATIONS.CLOSE
    },
    openingContextMessage() {
      if (this.openingContext.reason) return this.openingContext.reason
      if (this.openingContext.isFirstOpening) {
        return 'Primeira abertura da loja. Defina o saldo inicial.'
      }

      return `Valor preenchido com base no fechamento do caixa #${this.openingContext.previousCheckoutId}.`
    },
    canOverrideOpening() {
      return Boolean(
        this.openingContext?.isAdmin &&
          this.openingContext.expectedOpeningValue !== null
      )
    },
    isOpeningValueReadOnly() {
      if (!this.isOpeningCheckout) return false
      if (this.openingContext?.isFirstOpening) {
        return !this.openingContext.isAdmin
      }
      if (this.canOverrideOpening) return !this.form.openingOverrideEnabled
      return true
    },
    openingHasDifference() {
      if (!this.isOpeningCheckout || !this.form.openingOverrideEnabled) {
        return false
      }

      const expected = Number(this.openingContext?.expectedOpeningValue)
      const informed = parseCurrency(this.form.openingValue)
      return (
        Number.isFinite(expected) &&
        Number.isFinite(informed) &&
        Math.round((informed - expected) * 100) !== 0
      )
    },
    closingExpectedValue() {
      return Number(this.form.closingExpectedValue || 0)
    },
    closingDifference() {
      const informed = parseCurrency(this.form.closingValue)
      if (!Number.isFinite(informed)) return 0
      return Math.round((informed - this.closingExpectedValue) * 100) / 100
    },
    closingDifferenceAbsolute() {
      return Math.abs(this.closingDifference)
    },
    closingHasDifference() {
      return this.isClosingCheckout && this.closingDifference !== 0
    },
    closingDifferenceLabel() {
      if (this.closingDifference < 0) return 'Falta encontrada'
      if (this.closingDifference > 0) return 'Sobra encontrada'
      return 'Diferença'
    },
    closingDifferenceClass() {
      return {
        'text-danger': this.closingDifference < 0,
        'text-success': this.closingDifference > 0
      }
    },
    cameraTargetLabel() {
      return this.cameraTarget === 'closingNotebookPhotos'
        ? 'Foto do caderno de fechamento'
        : 'Foto do comprovante de maquininha'
    },
    closingIntroText() {
      const hasPhotoAttachments =
        this.hasCheckoutClosingNotebookPhotos ||
        this.hasCheckoutMachineReceiptPhotos

      if (hasPhotoAttachments && this.hasActiveProducts) {
        return 'Informe o saldo contado, confira o estoque final e anexe os registros configurados pela loja.'
      }
      if (hasPhotoAttachments) {
        return 'Informe o saldo contado e anexe os registros configurados pela loja.'
      }
      if (this.hasActiveProducts) {
        return 'Informe o saldo contado e confira o estoque final para concluir o fechamento.'
      }

      return 'Informe o saldo contado para concluir o fechamento.'
    },
    hasCheckoutClosingNotebookPhotos() {
      if (
        this.storeConfig &&
        typeof this.storeConfig.hasCheckoutClosingNotebookPhotos !== 'undefined'
      ) {
        return Boolean(this.storeConfig.hasCheckoutClosingNotebookPhotos)
      }

      return this.storeHasCheckoutClosingNotebookPhotos
    },
    isCheckoutClosingNotebookPhotoRequired() {
      if (
        this.storeConfig &&
        typeof this.storeConfig.isCheckoutClosingNotebookPhotoRequired !==
          'undefined'
      ) {
        return Boolean(this.storeConfig.isCheckoutClosingNotebookPhotoRequired)
      }

      return this.storeIsCheckoutClosingNotebookPhotoRequired
    },
    hasCheckoutMachineReceiptPhotos() {
      if (
        this.storeConfig &&
        typeof this.storeConfig.hasCheckoutMachineReceiptPhotos !== 'undefined'
      ) {
        return Boolean(this.storeConfig.hasCheckoutMachineReceiptPhotos)
      }

      return this.storeHasCheckoutMachineReceiptPhotos
    },
    isCheckoutMachineReceiptPhotoRequired() {
      if (
        this.storeConfig &&
        typeof this.storeConfig.isCheckoutMachineReceiptPhotoRequired !==
          'undefined'
      ) {
        return Boolean(this.storeConfig.isCheckoutMachineReceiptPhotoRequired)
      }

      return this.storeIsCheckoutMachineReceiptPhotoRequired
    },
    field() {
      return this.isOpeningCheckout ? 'openingValue' : 'closingValue'
    },
    productField() {
      return this.isOpeningCheckout
        ? 'openingProductCount'
        : 'closingProductCount'
    },
    validationClass() {
      const { $dirty, $invalid } = this.$v.form[this.field]

      return {
        'is-invalid': $dirty && $invalid
      }
    },
    openingReasonValidationClass() {
      const { $dirty, $invalid } = this.$v.form.openingOverrideReason
      return { 'is-invalid': $dirty && $invalid }
    },
    closingReasonValidationClass() {
      const { $dirty, $invalid } = this.$v.form.closingDifferenceReason
      return { 'is-invalid': $dirty && $invalid }
    },
    productValidationClass() {
      const { $dirty, $invalid } = this.$v.form[this.productField]

      return {
        'is-invalid': $dirty && $invalid
      }
    },
    closingNotebookValidationClass() {
      const { $dirty, $invalid } = this.$v.form.closingNotebookPhotos

      return {
        'is-invalid': $dirty && $invalid
      }
    },
    machineReceiptValidationClass() {
      const { $dirty, $invalid } = this.$v.form.machineReceiptPhotos

      return {
        'is-invalid': $dirty && $invalid
      }
    },
    closingNotebookInvalidMessage() {
      return this.photoInvalidMessage(
        this.form.closingNotebookPhotos,
        this.isCheckoutClosingNotebookPhotoRequired,
        'closingNotebookPhotos'
      )
    },
    closingNotebookHelpText() {
      return this.isCheckoutClosingNotebookPhotoRequired
        ? 'Envie de 1 a 3 fotos para concluir o fechamento.'
        : 'Você pode anexar até 3 fotos.'
    },
    machineReceiptInvalidMessage() {
      return this.photoInvalidMessage(
        this.form.machineReceiptPhotos,
        this.isCheckoutMachineReceiptPhotoRequired,
        'machineReceiptPhotos'
      )
    },
    machineReceiptHelpText() {
      return this.isCheckoutMachineReceiptPhotoRequired
        ? 'Envie de 1 a 3 fotos para concluir o fechamento.'
        : 'Você pode anexar até 3 fotos.'
    }
  },
  watch: {
    form: {
      handler(value) {
        this.$emit('input', value)
      },
      deep: true
    }
  },
  destroyed() {
    this.form = {
      openingValue: null,
      openingOverrideEnabled: false,
      openingOverrideReason: null,
      openingProductCount: null,
      closingValue: null,
      closingExpectedValue: null,
      closingDifferenceReason: null,
      closingProductCount: null,
      closingNotebookPhotos: [],
      closingNotebookDescriptions: [],
      machineReceiptPhotos: [],
      machineReceiptDescriptions: []
    }
  },
  beforeDestroy() {
    this.closePhotoCamera()
  },
  methods: {
    onOpeningOverrideChange(enabled) {
      if (enabled) return
      this.form.openingValue = formatCurrencyInput(
        this.openingContext?.expectedOpeningValue || 0
      )
      this.form.openingOverrideReason = null
      this.$v.form.openingOverrideReason.$reset()
    },
    photoCountLabel(files = []) {
      const count = files.length
      if (!count) return 'Nenhuma foto selecionada'
      return `${count} ${
        count === 1 ? 'foto selecionada' : 'fotos selecionadas'
      }`
    },
    async onPhotosInput(key, files) {
      const selectedFiles = Array.isArray(files)
        ? files
        : files
        ? Array.from(files)
        : []
      const requestId = this.photoProcessingRequestId[key] + 1
      this.$set(this.photoProcessingRequestId, key, requestId)
      this.$set(this.photoInputErrors, key, null)
      this.$set(this.photoProcessing, key, selectedFiles.length > 0)
      this.$v.form[key].$touch()

      if (!selectedFiles.length) {
        this.setPhotoFiles(key, [])
        return
      }

      try {
        const preparedFiles = await Promise.all(
          selectedFiles
            .slice(0, MAX_PHOTOS_PER_TYPE)
            .map((file) => this.preparePhotoFile(file))
        )

        if (requestId === this.photoProcessingRequestId[key]) {
          this.setPhotoFiles(key, preparedFiles)
        }
      } catch (error) {
        if (requestId === this.photoProcessingRequestId[key]) {
          this.setPhotoFiles(key, [])
          this.$set(
            this.photoInputErrors,
            key,
            error.message || 'Não foi possível preparar a foto para envio.'
          )
        }
      } finally {
        if (requestId === this.photoProcessingRequestId[key]) {
          this.$set(this.photoProcessing, key, false)
        }
      }
    },
    async preparePhotoFile(file) {
      if (!file?.type?.startsWith('image/')) {
        throw new Error('Selecione apenas arquivos de imagem.')
      }
      if (file.size > MAX_PHOTO_SIZE) {
        throw new Error('Cada foto original deve ter no máximo 20 MB.')
      }

      const image = await this.loadPhotoImage(file)
      if (
        file.size <= CLOUDINARY_SAFE_PHOTO_SIZE &&
        Math.max(image.width, image.height) <= CAMERA_MAX_DIMENSION
      ) {
        return file
      }

      const scale = Math.min(
        1,
        CAMERA_MAX_DIMENSION / Math.max(image.width, image.height)
      )
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(image.width * scale)
      canvas.height = Math.round(image.height * scale)
      const context = canvas.getContext('2d')
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(image, 0, 0, canvas.width, canvas.height)

      let blob = await this.canvasToJpegBlob(canvas, 0.85)
      if (blob.size > CLOUDINARY_SAFE_PHOTO_SIZE) {
        blob = await this.canvasToJpegBlob(canvas, 0.65)
      }
      if (blob.size > CLOUDINARY_SAFE_PHOTO_SIZE) {
        throw new Error(
          'A foto continuou muito grande após a otimização. Tente outra imagem.'
        )
      }

      const baseName = file.name.replace(/\.[^.]+$/, '') || 'foto'
      return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
    },
    loadPhotoImage(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()

        image.onload = () => {
          URL.revokeObjectURL(url)
          resolve(image)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(
            new Error(
              'Este formato não pôde ser otimizado. Use uma imagem JPEG ou PNG.'
            )
          )
        }
        image.src = url
      })
    },
    canvasToJpegBlob(canvas, quality) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob)
            else reject(new Error('Não foi possível otimizar a foto.'))
          },
          'image/jpeg',
          quality
        )
      })
    },
    setPhotoFiles(key, files) {
      const limitedFiles = files.slice(0, MAX_PHOTOS_PER_TYPE)
      const descriptionKey =
        key === 'closingNotebookPhotos'
          ? 'closingNotebookDescriptions'
          : 'machineReceiptDescriptions'

      this.$set(this.form, key, limitedFiles)
      this.$set(
        this.form,
        descriptionKey,
        limitedFiles.map((_, index) => this.form[descriptionKey]?.[index] || '')
      )
    },
    async startPhotoCamera(target) {
      const currentFiles = this.form[target] || []

      this.stopPhotoCameraStream()
      this.clearCameraPreview()
      this.cameraTarget = target
      this.cameraCapturedFile = null
      this.cameraError = null

      if (currentFiles.length >= MAX_PHOTOS_PER_TYPE) {
        this.cameraError = 'O limite de 3 fotos já foi atingido.'
        return
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError =
          'Este dispositivo ou navegador não permite abrir a câmera.'
        return
      }

      this.cameraLoading = true
      const requestId = ++this.cameraRequestId

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' }
          },
          audio: false
        })

        if (requestId !== this.cameraRequestId || !this.cameraTarget) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        this.cameraStream = stream
        this.cameraActive = true

        await this.$nextTick()
        if (this.$refs.checkoutPhotoVideo) {
          this.$refs.checkoutPhotoVideo.srcObject = stream
          await this.$refs.checkoutPhotoVideo.play()
        }
      } catch (error) {
        if (requestId === this.cameraRequestId) {
          this.cameraActive = false
          this.cameraError = this.getCameraErrorMessage(error)
        }
      } finally {
        if (requestId === this.cameraRequestId) {
          this.cameraLoading = false
        }
      }
    },
    async capturePhotoFromCamera() {
      const video = this.$refs.checkoutPhotoVideo

      if (!video || !video.videoWidth || !video.videoHeight) {
        this.cameraError =
          'A câmera ainda está carregando. Aguarde e tente novamente.'
        return
      }

      const scale = Math.min(
        1,
        CAMERA_MAX_DIMENSION / Math.max(video.videoWidth, video.videoHeight)
      )
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(video.videoWidth * scale)
      canvas.height = Math.round(video.videoHeight * scale)
      const context = canvas.getContext('2d')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      const requestId = this.cameraRequestId
      const target = this.cameraTarget

      let blob
      try {
        blob = await this.canvasToJpegBlob(canvas, 0.88)
        if (blob.size > CLOUDINARY_SAFE_PHOTO_SIZE) {
          blob = await this.canvasToJpegBlob(canvas, 0.65)
        }
      } catch (error) {
        this.cameraError = error.message || 'Não foi possível gerar a foto.'
        return
      }

      if (requestId !== this.cameraRequestId || target !== this.cameraTarget) {
        return
      }

      if (blob.size > CLOUDINARY_SAFE_PHOTO_SIZE) {
        this.cameraError = 'A foto gerada continuou muito grande.'
        return
      }

      const prefix =
        target === 'closingNotebookPhotos'
          ? 'caderno-fechamento'
          : 'comprovante-maquininha'
      this.cameraCapturedFile = new File(
        [blob],
        `${prefix}-${Date.now()}.jpg`,
        { type: 'image/jpeg' }
      )
      this.cameraPreviewUrl = URL.createObjectURL(blob)
      this.cameraError = null
      this.stopPhotoCameraStream()
    },
    confirmCameraPhoto() {
      if (!this.cameraTarget || !this.cameraCapturedFile) return

      const currentFiles = this.form[this.cameraTarget] || []
      if (currentFiles.length >= MAX_PHOTOS_PER_TYPE) {
        this.cameraError = 'O limite de 3 fotos já foi atingido.'
        return
      }

      this.setPhotoFiles(this.cameraTarget, [
        ...currentFiles,
        this.cameraCapturedFile
      ])
      this.$v.form[this.cameraTarget].$touch()
      this.closePhotoCamera()
    },
    retakePhoto() {
      const target = this.cameraTarget
      if (target) this.startPhotoCamera(target)
    },
    closePhotoCamera() {
      this.cameraRequestId += 1
      this.stopPhotoCameraStream()
      this.clearCameraPreview()
      this.cameraTarget = null
      this.cameraCapturedFile = null
      this.cameraError = null
      this.cameraLoading = false
    },
    stopPhotoCameraStream() {
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach((track) => track.stop())
      }
      this.cameraStream = null
      this.cameraActive = false
    },
    clearCameraPreview() {
      if (this.cameraPreviewUrl) {
        URL.revokeObjectURL(this.cameraPreviewUrl)
      }
      this.cameraPreviewUrl = null
    },
    getCameraErrorMessage(error) {
      if (error?.name === 'NotAllowedError') {
        return 'Permita o acesso à câmera para tirar a foto.'
      }
      if (error?.name === 'NotFoundError') {
        return 'Nenhuma câmera foi encontrada neste dispositivo.'
      }
      if (error?.name === 'NotReadableError') {
        return 'A câmera está em uso por outro aplicativo.'
      }

      return 'Não foi possível abrir a câmera. Verifique as permissões.'
    },
    validatePhotos(files = [], isRequired) {
      if (isRequired && files.length === 0) return false
      if (files.length > MAX_PHOTOS_PER_TYPE) return false

      return files.every(
        (file) =>
          file?.type?.startsWith('image/') && file.size <= MAX_PHOTO_SIZE
      )
    },
    photoInvalidMessage(files = [], isRequired, key) {
      if (this.photoProcessing[key]) {
        return 'Aguarde a otimização das fotos.'
      }

      if (isRequired && files.length === 0) {
        return 'Envie ao menos uma foto.'
      }

      if (files.length > MAX_PHOTOS_PER_TYPE) {
        return 'Envie no maximo 3 fotos.'
      }

      return 'As fotos devem ser imagens com no maximo 20 MB.'
    }
  }
}
</script>

<style scoped>
.checkout-closing-form {
  display: grid;
  gap: 1rem;
}

.closing-intro {
  align-items: center;
  background: #eef6ff;
  border: 1px solid #cfe2ff;
  border-radius: 0.5rem;
  color: #254b76;
  display: flex;
  gap: 0.875rem;
  padding: 1rem;
}

.closing-intro p {
  color: #5d7188;
  font-size: 0.875rem;
}

.closing-intro-icon {
  align-items: center;
  background: #fff;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 2.5rem;
  height: 2.5rem;
  justify-content: center;
}

.closing-section,
.closing-upload-section {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  margin: 0;
  padding: 1rem;
}

.closing-uploads-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(17.5rem, 1fr));
}

.closing-upload-section.is-invalid {
  border-color: #dc3545;
}

.closing-section-heading,
.closing-upload-heading {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.875rem;
  min-width: 0;
}

.closing-section-heading > div,
.closing-upload-title {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.closing-section-heading small,
.closing-upload-title small {
  color: #6c757d;
  font-size: 0.8rem;
  line-height: 1.3;
}

.closing-section-icon {
  align-items: center;
  background: #f1f3f5;
  border-radius: 0.4rem;
  color: #495057;
  display: inline-flex;
  flex: 0 0 2rem;
  height: 2rem;
  justify-content: center;
}

.closing-upload-heading .badge {
  border: 1px solid #dee2e6;
  flex: 0 0 auto;
}

.closing-balance-summary {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.4rem;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 0.875rem;
  padding: 0.75rem;
}

.closing-balance-summary > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.closing-balance-summary small {
  color: #6c757d;
  font-size: 0.75rem;
}

.photo-selection-status {
  color: #6c757d;
  display: block;
  margin-top: 0.5rem;
}

.photo-upload-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.625rem;
}

.closing-camera-panel {
  background: #fff;
  border: 1px solid #cfe2ff;
  border-radius: 0.5rem;
  padding: 1rem;
}

.closing-camera-heading {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.875rem;
}

.closing-camera-heading > div {
  display: flex;
  flex-direction: column;
}

.closing-camera-heading small {
  color: #6c757d;
  margin-top: 0.2rem;
}

.closing-camera-loading {
  align-items: center;
  color: #6c757d;
  display: flex;
  justify-content: center;
  min-height: 10rem;
}

.closing-camera-media {
  background: #111;
  border-radius: 0.5rem;
  display: block;
  max-height: 55vh;
  object-fit: contain;
  width: 100%;
}

.closing-camera-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.875rem;
}

.checkout-closing-form ::v-deep .custom-file-label {
  background: #f8f9fa;
  border-style: dashed;
}

.photo-description ::v-deep .form-control {
  background: #f8f9fa;
}

.checkout-closing-form ::v-deep .custom-file-label,
.checkout-closing-form ::v-deep .form-control,
.checkout-closing-form ::v-deep .input-group-text {
  min-height: 2.65rem;
}

@media (max-width: 575.98px) {
  .closing-uploads-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .closing-intro,
  .closing-section,
  .closing-upload-section {
    padding: 0.875rem;
  }

  .closing-upload-heading {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .closing-upload-heading .badge {
    margin-left: 2.75rem;
  }

  .closing-camera-actions .btn {
    flex: 1 1 auto;
  }

  .closing-balance-summary {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
